import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      position: "CEO, TechStart Solutions",
      company: "TechStart Solutions",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      content: `Working with Zentirx Infotech was an absolute game-changer for our business. They delivered a comprehensive e-commerce platform that exceeded our expectations in every way.

The team's attention to detail, technical expertise, and commitment to deadlines made the entire process seamless. Our online sales increased by 300% within the first quarter after launch.`,
      rating: 5,
      project: "E-Commerce Platform Development"
    },
    {
      id: 2,
      name: "Michael Chen",
      position: "CTO, HealthTech Innovations",
      company: "HealthTech Innovations",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      content: `The mobile app developed by Zentirx has revolutionized how we manage patient care. The intuitive design and robust functionality have significantly improved our operational efficiency.

Their team understood our complex healthcare requirements and delivered a solution that not only met but exceeded our regulatory compliance needs. Highly recommended!`,
      rating: 5,
      project: "Healthcare Management App"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      position: "Founder, EduLearn Platform",
      company: "EduLearn Platform",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      content: `Zentirx Infotech transformed our vision of an online learning platform into reality. The LMS they built is user-friendly, scalable, and packed with features that our students love.

The project was completed on time and within budget. Their ongoing support and maintenance services have been exceptional. We couldn't have asked for a better technology partner.`,
      rating: 5,
      project: "Learning Management System"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const goToSlide = (index) => setCurrentIndex(index);
  const goToPrevious = () =>
    setCurrentIndex(currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1);
  const goToNext = () =>
    setCurrentIndex(currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: index * 0.1 }}
      >
        <Icon
          name="Star"
          size={20}
          className={index < rating ? "text-accent fill-current" : "text-secondary-300"}
        />
      </motion.div>
    ));
  };

  return (
    <section className="py-8 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
            What Our Clients Say
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Don't just take our word for it. Here's what our satisfied clients have to say.
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-2xl">
            <motion.div
              className="flex"
              animate={{ x: `-${currentIndex * 100}%` }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              {testimonials.map((t) => (
                <div key={t.id} className="w-full flex-shrink-0">
                  <motion.div
                    className="bg-background border border-border rounded-2xl p-8 lg:p-12 mx-4"
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                  >
                    <motion.div
                      className="flex justify-center mb-6"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", duration: 0.6 }}
                    >
                      <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center">
                        <Icon name="Quote" size={24} className="text-primary" />
                      </div>
                    </motion.div>

                    <div className="flex justify-center mb-6">
                      <div className="flex space-x-1">{renderStars(t.rating)}</div>
                    </div>

                    <motion.blockquote
                      className="text-lg text-text-primary text-center mb-8 leading-relaxed"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7 }}
                    >
                      "{t.content}"
                    </motion.blockquote>

                    <div className="flex items-center justify-center">
                      <motion.div
                        className="flex items-center space-x-4"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Image
                          src={t.avatar}
                          alt={t.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-primary-100"
                        />

                        <div className="text-center sm:text-left">
                          <h4 className="font-semibold text-text-primary">{t.name}</h4>
                          <p className="text-text-secondary text-sm">{t.position}</p>
                          <p className="text-primary text-sm font-medium">{t.company}</p>
                        </div>
                      </motion.div>
                    </div>

                    <motion.div
                      className="text-center mt-6"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <span className="inline-block px-4 py-2 bg-primary-50 text-primary text-sm font-medium rounded-full">
                        {t.project}
                      </span>
                    </motion.div>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </div>

          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 w-12 h-12 bg-surface border border-border rounded-full flex items-center justify-center text-text-secondary hover:text-primary hover:border-primary transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 w-12 h-12 bg-surface border border-border rounded-full flex items-center justify-center text-text-secondary hover:text-primary hover:border-primary transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Icon name="ChevronRight" size={20} />
          </button>

          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-primary scale-125'
                    : 'bg-secondary-300 hover:bg-secondary-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
